/**
 * Stakeholder coverage panel (2026-08-05 ROI pack) — who on the buying
 * committee is engaging and who has gone quiet, rendered inside the
 * seller's Decision Room view. Single-threading is the classic silent
 * deal-killer; this is the panel that makes it un-silent.
 *
 * Data: GET /api/buyer-spaces/:id/coverage (committee × per-recipient
 * trailer views × room activity, classified engaged/dormant/silent).
 */

import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const STATUS_STYLE = {
  engaged: { label: 'Engaged', color: '#16a34a', bg: 'rgba(22,163,74,0.1)' },
  dormant: { label: 'Gone quiet', color: '#d97706', bg: 'rgba(217,119,6,0.1)' },
  silent: { label: 'Never engaged', color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
};

export default function CoveragePanel({ authToken, spaceId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/coverage`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => { if (!cancelled) setData(j); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [spaceId, authToken]);

  // Soft-fail: coverage is an enhancement, never a blocker for the room.
  if (error || !data) return null;
  if (!data.people.length) {
    return (
      <div style={{ padding: '14px 16px', border: '1px dashed #e2e8f0', borderRadius: 12, fontSize: 13, color: 'var(--room-text-muted, #64748b)', marginBottom: 16 }}>
        Add the buying committee under <strong>People</strong> to track who's engaging — multi-threaded
        deals close, single-threaded ones stall.
      </div>
    );
  }

  const { summary } = data;
  return (
    <div style={{ border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 14, padding: 16, marginBottom: 16, background: 'var(--room-card, #fff)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)' }}>Committee coverage</div>
        <div style={{
          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
          color: summary.multiThreaded ? '#16a34a' : '#d97706',
          background: summary.multiThreaded ? 'rgba(22,163,74,0.1)' : 'rgba(217,119,6,0.12)',
        }}>
          {summary.multiThreaded
            ? `Multi-threaded · ${summary.engaged} of ${summary.committeeSize} engaged`
            : `Single-threaded — ${summary.engaged} of ${summary.committeeSize} engaged`}
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        {data.people.map((p) => {
          const s = STATUS_STYLE[p.status] || STATUS_STYLE.silent;
          return (
            <div key={p.personId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: '1px solid var(--room-border-soft, #f1f5f9)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--room-text-strong, #0f172a)' }}>{p.name}</span>
                {p.title && <span style={{ fontSize: 12, color: 'var(--room-text-subtle, #94a3b8)', marginLeft: 8 }}>{p.title}</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--room-text-muted, #64748b)', whiteSpace: 'nowrap' }}>
                {p.trailerViews > 0 && `${p.trailerViews} view${p.trailerViews === 1 ? '' : 's'}`}
                {p.trailerViews > 0 && p.roomEvents > 0 && ' · '}
                {p.roomEvents > 0 && `${p.roomEvents} room action${p.roomEvents === 1 ? '' : 's'}`}
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color: s.color, background: s.bg, whiteSpace: 'nowrap' }}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
